import {
  REPORT_NOTE_MAX,
  REPORT_REASONS,
  type AdminReport,
  type ReportReason,
  type ReportResolution,
  type ReportTargetKind,
} from '@fewerbags/schema';

/** Reason choices for the report dialog, in display order. */
export const REASON_LABELS: Record<ReportReason, string> = {
  inaccurate: 'Wrong or out of date',
  offensive: 'Offensive or inappropriate',
  copyright: 'Copied without permission',
  spam: 'Spam or advertising',
  other: 'Something else',
};

export const REASON_OPTIONS = REPORT_REASONS.map((value) => ({
  value,
  label: REASON_LABELS[value],
}));

/** Reasons that make sense for each kind of target (prices are never "copyright"). */
export function reasonOptionsFor(kind: ReportTargetKind) {
  if (kind === 'store_product_price' || kind === 'store_product_location') {
    return REASON_OPTIONS.filter((o) => o.value === 'inaccurate' || o.value === 'other');
  }
  return REASON_OPTIONS;
}

/** Trimmed note, or undefined when empty; null when it is too long. */
export function normaliseNote(note: string): string | undefined | null {
  const t = note.trim();
  if (t.length === 0) return undefined;
  return t.length > REPORT_NOTE_MAX ? null : t;
}

/** What the reporter sees after a report went through. */
export function reportSentMessage(kind: ReportTargetKind, hidden: boolean): string {
  if (hidden) {
    return kind === 'recipe'
      ? 'Thanks. This recipe is hidden from the library until we review it.'
      : 'Thanks. This photo is hidden until we review it.';
  }
  return 'Thanks. We will take a look.';
}

export const TARGET_KIND_LABELS: Record<ReportTargetKind, string> = {
  ingredient_photo: 'Ingredient photo',
  recipe: 'Public recipe',
  store_product_price: 'Price',
  store_product_location: 'Location',
};

/** Admin resolve actions offered for a report batch, primary first. */
export function resolveActionsFor(
  report: Pick<AdminReport, 'targetKind' | 'target'>,
): { resolution: ReportResolution; label: string }[] {
  switch (report.targetKind) {
    case 'ingredient_photo':
      return [
        report.target.hidden
          ? { resolution: 'hidden', label: 'Keep hidden' }
          : { resolution: 'hidden', label: 'Hide' },
        { resolution: 'removed', label: 'Delete photo' },
        {
          resolution: 'dismissed',
          label: report.target.hidden ? 'Dismiss and show again' : 'Dismiss',
        },
      ];
    case 'recipe':
      return [
        report.target.hidden
          ? { resolution: 'hidden', label: 'Keep hidden' }
          : { resolution: 'hidden', label: 'Hide' },
        { resolution: 'removed', label: 'Remove from library' },
        {
          resolution: 'dismissed',
          label: report.target.hidden ? 'Dismiss and show again' : 'Dismiss',
        },
      ];
    case 'store_product_price':
    case 'store_product_location':
      return [
        { resolution: 'removed', label: 'Remove reported value' },
        { resolution: 'dismissed', label: 'Dismiss' },
      ];
  }
}

export const RESOLUTION_LABELS: Record<ReportResolution, string> = {
  hidden: 'Hidden',
  dismissed: 'Dismissed',
  removed: 'Removed',
};

/** "3 households" / "1 household". */
export function householdCountLabel(n: number): string {
  return `${n} ${n === 1 ? 'household' : 'households'}`;
}
